import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon, ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const FoodSourceSelector = ({ selectedSources, onSourcesChange, producerNames, animalNames, disabled }) => {
  const handleToggleSource = (source) => {
    if (selectedSources.includes(source)) {
      onSourcesChange(selectedSources.filter(s => s !== source));
    } else {
      onSourcesChange([...selectedSources, source]);
    }
  };

  const renderButton = (symbol) => {
    const isSelected = selectedSources.includes(symbol);

    return (
      <motion.button
        key={symbol}
        onClick={() => handleToggleSource(symbol)}
        disabled={disabled}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={false}
        animate={{
          backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.15)' : 'rgba(30, 41, 59, 0.7)',
          borderColor: isSelected ? 'rgba(96, 165, 250, 0.5)' : 'rgba(51, 65, 85, 0.5)',
          boxShadow: isSelected 
            ? '0 0 20px rgba(59, 130, 246, 0.4), inset 0 0 10px rgba(59, 130, 246, 0.2), 0 0 0 1px rgba(96, 165, 250, 0.5)' 
            : '0 0 10px rgba(51, 65, 85, 0.2), inset 0 0 5px rgba(51, 65, 85, 0.1)'
        }}
        className={`
          w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex flex-col items-center justify-center
          border-2 transition-all duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          backdrop-blur-sm backdrop-filter
          ${isSelected ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}
        `}
      >
        <motion.span 
          className="text-lg sm:text-xl font-bold"
          animate={{
            color: isSelected ? 'rgb(147, 197, 253)' : 'rgb(148, 163, 184)'
          }}
        >
          {symbol}
        </motion.span>
      </motion.button>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-8">
      {/* Producers Grid */}
      {[1, 2, 3].some(num => renderButton(num.toString())) && (
        <div className="space-y-2">
          <div className="text-sm text-blue-400 text-center">Choose producers that feed this animal (if any)</div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-6">
            {[1, 2, 3].map(num => 
              renderButton(
                num.toString()
              )
            )}
          </div>
        </div>
      )}

      {/* Animals Grid */}
      {'ABCDE'.split('').some(letter => renderButton(letter)) && (
        <div className="space-y-2">
          <div className="text-sm text-blue-400 text-center">Choose animals that eat this producer</div>
          <div className="grid grid-cols-5 gap-1 sm:gap-6 max-w-full sm:max-w-[300px] mx-auto">
            {/* First Row (A-E) */}
            {'ABCDE'.split('').map((letter, idx) =>
              renderButton(
                letter
              )
            )}
            {/* Second Row (F-J) */}
            {'FGHIJ'.split('').map((letter, idx) =>
              renderButton(
                letter
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Add SolveEcosystemButton component
const SolveEcosystemButton = ({ onSolve, isLoading, progress }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 sm:mt-8 flex justify-center"
    >
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onSolve}
        disabled={isLoading}
        className={`
          relative overflow-hidden
          px-6 sm:px-8 py-3 sm:py-4 rounded-xl
          bg-gradient-to-r from-blue-600/20 to-blue-800/20
          border border-blue-500/30
          backdrop-blur-sm
          ${isLoading ? 'cursor-wait' : 'cursor-pointer'}
          group
        `}
      >
        {/* Knight Rider effect */}
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: "linear"
          }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent"
        />

        {/* Glow effect */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.5, 0] }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: "easeInOut"
          }}
          className="absolute inset-0 bg-blue-500/10 blur-xl"
        />

        {/* Button content */}
        <div className="relative flex items-center gap-2 sm:gap-3">
          <span className="text-base sm:text-lg font-semibold text-blue-300">
            {isLoading ? 'Solving Ecosystem...' : 'Solve Ecosystem'}
          </span>
          
          {isLoading && (
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '80px' }}
              className="h-2 bg-blue-500/30 rounded-full overflow-hidden hidden sm:block"
            >
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: "linear"
                }}
                className="h-full w-1/2 bg-blue-500"
              />
            </motion.div>
          )}
        </div>

        {/* Progress indicator */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute -bottom-6 left-0 right-0 text-center text-sm text-blue-400"
          >
            {progress}%
          </motion.div>
        )}
      </motion.button>
    </motion.div>
  );
};

const ManualEntry = ({ initialData, onDataChange, onStateChange, setActiveStep }) => {
  // Start with empty data
  const [metadata, setMetadata] = useState({
    ecosystemName: '',
    minDepth: '',
    maxDepth: '',
    minTemp: '',
    maxTemp: ''
  });

  const [producers, setProducers] = useState([
    {
      id: 'P1',
      Name: '',
      'Calories Provided': '',
      'Eaten By': []
    },
    {
      id: 'P2',
      Name: '',
      'Calories Provided': '',
      'Eaten By': []
    },
    {
      id: 'P3',
      Name: '',
      'Calories Provided': '',
      'Eaten By': []
    }
  ]);

  const [animals, setAnimals] = useState([
    {
      id: 'A1',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    },
    {
      id: 'A2',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    },
    {
      id: 'A3',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    },
    {
      id: 'A4',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    },
    {
      id: 'A5',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    },
    {
      id: 'A6',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    },
    {
      id: 'A7',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    },
    {
      id: 'A8',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    },
    {
      id: 'A9',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    },
    {
      id: 'A10',
      Name: '',
      'Calories Needed': '',
      'Calories Provided': '',
      'Food Sources': []
    }
  ]);

  // Add prefill data
  const coralEcosystemData = {
    metadata: {
      ecosystemName: 'Coral Ecosystem 1',
      minDepth: '0',
      maxDepth: '30',
      minTemp: '20',
      maxTemp: '30'
    },
    producers: [
      {
        id: 'P1',
        Name: 'Common Eel Grass',
        'Calories Provided': 4950,
        'Eaten By': ['Blue Striped Angelfish', 'Bicolour Parrotfish', 'Hawksbill Sea Turtle', 'Queen Angelfish']
      },
      {
        id: 'P2',
        Name: 'Fire Coral',
        'Calories Provided': 5850,
        'Eaten By': ['Queen Parrotfish', 'Bicolour Parrotfish', 'Foxface Rabbitfish', 'Hawksbill Sea Turtle']
      },
      {
        id: 'P3',
        Name: 'Widgeon Grass',
        'Calories Provided': 4950,
        'Eaten By': ['Bicolour Parrotfish', 'Foxface Rabbitfish', 'Gem Tang']
      }
    ],
    animals: [
      {
        id: 'A1',
        Name: 'Bicolour Parrotfish',
        'Calories Needed': 8800,
        'Calories Provided': 3400,
        'Food Sources': ['Widgeon Grass', 'Common Eel Grass', 'Fire Coral']
      },
      {
        id: 'A2',
        Name: 'Blue Striped Angelfish',
        'Calories Needed': 4550,
        'Calories Provided': 3750,
        'Food Sources': ['Common Eel Grass']
      },
      {
        id: 'A3',
        Name: 'Common Dolphinfish',
        'Calories Needed': 2100,
        'Calories Provided': 2150,
        'Food Sources': ['Queen Parrotfish']
      },
      {
        id: 'A4',
        Name: 'Foxface Rabbitfish',
        'Calories Needed': 4050,
        'Calories Provided': 800,
        'Food Sources': ['Widgeon Grass', 'Fire Coral']
      },
      {
        id: 'A5',
        Name: 'Gem Tang',
        'Calories Needed': 4900,
        'Calories Provided': 1250,
        'Food Sources': ['Widgeon Grass']
      },
      {
        id: 'A6',
        Name: 'Hawksbill Sea Turtle',
        'Calories Needed': 4950,
        'Calories Provided': 2800,
        'Food Sources': ['Blue Striped Angelfish', 'Queen Parrotfish', 'Foxface Rabbitfish', 'Common Eel Grass', 'Fire Coral']
      },
      {
        id: 'A7',
        Name: 'Queen Angelfish',
        'Calories Needed': 3100,
        'Calories Provided': 2600,
        'Food Sources': ['Blue Striped Angelfish', 'Queen Parrotfish', 'Common Eel Grass']
      },
      {
        id: 'A8',
        Name: 'Queen Parrotfish',
        'Calories Needed': 4700,
        'Calories Provided': 3800,
        'Food Sources': ['Fire Coral']
      },
      {
        id: 'A9',
        Name: 'Short-tail Stingray',
        'Calories Needed': 2050,
        'Calories Provided': 1450,
        'Food Sources': ['Common Dolphinfish', 'Foxface Rabbitfish']
      },
      {
        id: 'A10',
        Name: 'Wahoo',
        'Calories Needed': 2500,
        'Calories Provided': 1700,
        'Food Sources': ['Short-tail Stingray', 'Blue Striped Angelfish']
      }
    ]
  };

  // Add prefill handler
  const handlePrefill = () => {
    setMetadata(coralEcosystemData.metadata);
    setProducers(coralEcosystemData.producers);
    setAnimals(coralEcosystemData.animals);
    setNameMapping({
      producers: coralEcosystemData.producers.map(p => p.Name),
      animals: coralEcosystemData.animals.map(a => a.Name)
    });
  };

  const [expandedSections, setExpandedSections] = useState(['Producers', 'Animals']);
  const [errors, setErrors] = useState({});
  const [showHints, setShowHints] = useState(true);
  const [nameMapping, setNameMapping] = useState({
    producers: producers.map(p => p.Name),
    animals: animals.map(a => a.Name)
  });

  // Add state for solve button
  const [isSolving, setIsSolving] = useState(false);
  const [solveProgress, setSolveProgress] = useState(0);

  // If initialData changes (e.g., user navigates back), update state
  useEffect(() => {
    if (initialData) {
      setMetadata(initialData.metadata || {
        ecosystemName: '',
        minDepth: '',
        maxDepth: '',
        minTemp: '',
        maxTemp: ''
      });
      setProducers(initialData.producers || []);
      setAnimals(initialData.animals || []);
    }
  }, [initialData]);

  // Add these validation functions
  const validateCircularDependencies = (animals) => {
    const visited = new Set();
    const recursionStack = new Set();

    const hasCycle = (animalName) => {
      if (recursionStack.has(animalName)) return true;
      if (visited.has(animalName)) return false;

      visited.add(animalName);
      recursionStack.add(animalName);

      const animal = animals.find(a => a.Name === animalName);
      if (animal) {
        let foodSources = [];
        if (Array.isArray(animal['Food Sources'])) {
          foodSources = animal['Food Sources'];
        } else if (typeof animal['Food Sources'] === 'string') {
          foodSources = animal['Food Sources'].split(',').map(s => s.trim());
        } else {
          foodSources = [];
        }
        for (const source of foodSources) {
          if (hasCycle(source)) return true;
        }
      }

      recursionStack.delete(animalName);
      return false;
    };

    for (const animal of animals) {
      if (hasCycle(animal.Name)) {
        return `Circular dependency found in food chain involving ${animal.Name}`;
      }
    }
    return null;
  };

  const validateFoodSources = (animals, producers) => {
    // Create a set of valid species names, including both simple names and formatted names
    const allSpecies = new Set();
    
    // Add producer names in both formats
    producers.forEach((p, index) => {
      allSpecies.add(p.Name);  // Simple name
      allSpecies.add(`Producer ${index + 1} - ${p.Name}`);  // Formatted name
    });
    
    // Add animal names in both formats
    animals.forEach((a, index) => {
      allSpecies.add(a.Name);  // Simple name
      allSpecies.add(`Animal ${String.fromCharCode(65 + index)} - ${a.Name}`);  // Formatted name
    });

    for (const animal of animals) {
      // Handle both string and array inputs
      const sources = typeof animal['Food Sources'] === 'string' 
        ? animal['Food Sources'].split(',').map(s => s.trim())
        : Array.isArray(animal['Food Sources']) 
          ? animal['Food Sources']
          : [];
      
      for (const source of sources) {
        if (source && !allSpecies.has(source)) {
          return `Invalid food source "${source}" for ${animal.Name}`;
        }
      }
    }
    return null;
  };

  // Update validateAgainstCoralData to be stricter and more descriptive
  const validateAgainstCoralData = () => {
    const newErrors = {};
    
    // Add a global error if any field doesn't match
    let hasAnyMismatch = false;
    
    // Validate producer names and calories
    producers.forEach((producer, index) => {
      const expectedProducer = coralEcosystemData.producers[index];
      if (!producer.Name || producer.Name !== expectedProducer.Name) {
        hasAnyMismatch = true;
        newErrors[`producer-${index}-name`] = `This is a demo version. Please use the "Prefill with Coral Ecosystem 1" button to load the correct data.`;
      }
      if (!producer['Calories Provided'] || 
          producer['Calories Provided'] !== expectedProducer['Calories Provided']) {
        hasAnyMismatch = true;
        newErrors[`producer-${index}-calories`] = `This is a demo version. Please use the "Prefill with Coral Ecosystem 1" button to load the correct data.`;
      }
    });

    // Validate animal names, calories, and food sources
    animals.forEach((animal, index) => {
      const expectedAnimal = coralEcosystemData.animals[index];
      
      if (!animal.Name || animal.Name !== expectedAnimal.Name) {
        hasAnyMismatch = true;
        newErrors[`animal-${index}-name`] = `This is a demo version. Please use the "Prefill with Coral Ecosystem 1" button to load the correct data.`;
      }
      
      if (!animal['Calories Needed'] || 
          animal['Calories Needed'] !== expectedAnimal['Calories Needed']) {
        hasAnyMismatch = true;
        newErrors[`animal-${index}-caloriesIn`] = `This is a demo version. Please use the "Prefill with Coral Ecosystem 1" button to load the correct data.`;
      }
      
      if (!animal['Calories Provided'] || 
          animal['Calories Provided'] !== expectedAnimal['Calories Provided']) {
        hasAnyMismatch = true;
        newErrors[`animal-${index}-caloriesOut`] = `This is a demo version. Please use the "Prefill with Coral Ecosystem 1" button to load the correct data.`;
      }

      // Validate food sources
      if (!animal['Food Sources']?.length || 
          !arraysMatch(animal['Food Sources'], expectedAnimal['Food Sources'])) {
        hasAnyMismatch = true;
        newErrors[`animal-${index}-foodSources`] = `This is a demo version. Please use the "Prefill with Coral Ecosystem 1" button to load the correct data.`;
      }
    });

    // Validate metadata
    if (metadata.ecosystemName !== coralEcosystemData.metadata.ecosystemName ||
        metadata.minDepth !== coralEcosystemData.metadata.minDepth ||
        metadata.maxDepth !== coralEcosystemData.metadata.maxDepth ||
        metadata.minTemp !== coralEcosystemData.metadata.minTemp ||
        metadata.maxTemp !== coralEcosystemData.metadata.maxTemp) {
      hasAnyMismatch = true;
      newErrors.metadata = `This is a demo version. Please use the "Prefill with Coral Ecosystem 1" button to load the correct data.`;
    }

    // Add a global error message if any field doesn't match
    if (hasAnyMismatch) {
      newErrors.demoVersion = "This is a demo version that only works with Coral Ecosystem 1 data. Please use the 'Prefill with Coral Ecosystem 1' button to load the correct data.";
    }

    return newErrors;
  };

  // Helper function to compare arrays
  const arraysMatch = (arr1, arr2) => {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    const set1 = new Set(arr1);
    return arr2.every(item => set1.has(item));
  };

  // Update the validateData function
  const validateData = () => {
    const newErrors = {};
    
    // Validate metadata
    if (!metadata.ecosystemName) {
      newErrors.ecosystemName = 'Ecosystem name is required';
    }
    if (!metadata.minDepth) {
      newErrors.minDepth = 'Minimum depth is required';
    }
    if (!metadata.maxDepth) {
      newErrors.maxDepth = 'Maximum depth is required';
    }
    if (!metadata.minTemp) {
      newErrors.minTemp = 'Minimum temperature is required';
    }
    if (!metadata.maxTemp) {
      newErrors.maxTemp = 'Maximum temperature is required';
    }
    
    if (metadata.minDepth && metadata.maxDepth && parseInt(metadata.minDepth) >= parseInt(metadata.maxDepth)) {
      newErrors.depth = 'Maximum depth must be greater than minimum depth';
    }
    
    if (metadata.minTemp && metadata.maxTemp && parseInt(metadata.minTemp) >= parseInt(metadata.maxTemp)) {
      newErrors.temp = 'Maximum temperature must be greater than minimum temperature';
    }

    // Validate producers
    if (producers.length !== 3) {
      newErrors.producers = 'Exactly 3 producers are required';
    }
    
    producers.forEach((producer, index) => {
      if (!producer.Name) {
        newErrors[`producer-${index}-name`] = 'Name is required';
      }
      if (!producer['Calories Provided']) {
        newErrors[`producer-${index}-calories`] = 'Calories Provided is required';
      } else if (producer['Calories Provided'] <= 0) {
        newErrors[`producer-${index}-calories`] = 'Calories Provided must be greater than 0';
      }
    });

    // Validate animals
    if (animals.length !== 10) {
      newErrors.animals = 'Exactly 10 animals are required';
    }

    animals.forEach((animal, index) => {
      if (!animal.Name) {
        newErrors[`animal-${index}-name`] = 'Name is required';
      }
      
      if (!animal['Calories Needed']) {
        newErrors[`animal-${index}-caloriesIn`] = 'Calories Needed is required';
      } else if (animal['Calories Needed'] <= 0) {
        newErrors[`animal-${index}-caloriesIn`] = 'Calories Needed must be greater than 0';
      }
      
      if (!animal['Calories Provided']) {
        newErrors[`animal-${index}-caloriesOut`] = 'Calories Provided is required';
      } else if (animal['Calories Provided'] <= 0) {
        newErrors[`animal-${index}-caloriesOut`] = 'Calories Provided must be greater than 0';
      }
      
      if (!animal['Food Sources']?.length) {
        newErrors[`animal-${index}-foodSources`] = 'At least one food source is required';
      }
    });

    // Check for circular dependencies
    const circularError = validateCircularDependencies(animals);
    if (circularError) {
      newErrors.circularDependency = circularError;
    }

    // Validate food sources exist
    const foodSourceError = validateFoodSources(animals, producers);
    if (foodSourceError) {
      newErrors.invalidFoodSource = foodSourceError;
    }

    // Then validate against Coral Ecosystem data
    const coralErrors = validateAgainstCoralData();
    Object.assign(newErrors, coralErrors);

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  // Effect to validate and update parent
  useEffect(() => {
    // Only update QuickReference, not parent navigation/results
    onStateChange?.({
      producers,
      animals
    });
    validateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata, producers, animals, onStateChange]);

  const addProducer = () => {
    if (producers.length >= 3) return;
    const newProducer = {
      id: `P${producers.length + 1}`,
      Name: '',
      'Calories Provided': 0,
      'Eaten By': []
    };
    setProducers(prev => {
      const updated = [...prev, newProducer];
      return updated;
    });
  };

  const addAnimal = () => {
    if (animals.length >= 10) return;
    const newAnimal = {
      id: `A${animals.length + 1}`,
      Name: '',
      'Calories Needed': 0,
      'Calories Provided': 0,
      'Food Sources': []
    };
    setAnimals(prev => {
      const updated = [...prev, newAnimal];
      return updated;
    });
  };

  // Helper to convert name to symbol
  const getSymbolFromName = (name) => {
    if (!name) return '';
    
    // First check if it's a producer name
    const producerMatch = name.match(/^Producer ([123]) - (.+)$/);
    if (producerMatch) {
      return producerMatch[1];
    }
    
    // Then check if it's an animal name
    const animalMatch = name.match(/^Animal ([A-J]) - (.+)$/);
    if (animalMatch) {
      return animalMatch[1];
    }
    
    // If no match found, try to find in mappings
    const producerIdx = nameMapping.producers.indexOf(name);
    if (producerIdx !== -1) {
      return (producerIdx + 1).toString();
    }
    
    const animalIdx = nameMapping.animals.indexOf(name);
    if (animalIdx !== -1) {
      return String.fromCharCode(65 + animalIdx);
    }
    
    return '';
  };

  const updateProducer = (index, updates) => {
    if ('Name' in updates) {
      const newMapping = [...nameMapping.producers];
      newMapping[index] = updates.Name;
      setNameMapping({ ...nameMapping, producers: newMapping });
    }
    const updated = [...producers];
    const updatedProducer = { ...updated[index] };
    Object.assign(updatedProducer, updates);
    updated[index] = updatedProducer;
    setProducers(updated);
  };

  const updateAnimal = (index, updates) => {
    if ('Name' in updates) {
      const newMapping = [...nameMapping.animals];
      newMapping[index] = updates.Name;
      setNameMapping({ ...nameMapping, animals: newMapping });
    }
    const updated = [...animals];
    const updatedAnimal = { ...updated[index] };
    if ('Food Sources' in updates) {
      if (typeof updates['Food Sources'] === 'string') {
        updates['Food Sources'] = updates['Food Sources'].split(',').map(s => s.trim()).filter(Boolean);
      } else if (!Array.isArray(updates['Food Sources'])) {
        updates['Food Sources'] = [];
      }
    }
    Object.assign(updatedAnimal, updates);
    updated[index] = updatedAnimal;
    setAnimals(updated);
  };

  const removeProducer = (index) => {
    if (producers.length <= 1) return;
    const producerName = producers[index].Name;
    
    // Remove this producer from all animals' Food Sources
    animals.forEach(animal => {
      if (animal['Food Sources'].includes(producerName)) {
        const animalIndex = animals.findIndex(a => a.id === animal.id);
        const updatedAnimal = { ...animal };
        updatedAnimal['Food Sources'] = updatedAnimal['Food Sources'].filter(
          source => source !== producerName
        );
        updateAnimal(animalIndex, updatedAnimal);
      }
    });

    const updated = producers.filter((_, i) => i !== index);
    setProducers(updated);
  };

  const removeAnimal = (index) => {
    if (animals.length <= 10) return;
    const animalName = animals[index].Name;
    
    // Remove this animal from all producers' Eaten By lists
    producers.forEach(producer => {
      if (producer['Eaten By'].includes(animalName)) {
        const producerIndex = producers.findIndex(p => p.id === producer.id);
        const updatedProducer = { ...producer };
        updatedProducer['Eaten By'] = updatedProducer['Eaten By'].filter(
          eater => eater !== animalName
        );
        updateProducer(producerIndex, updatedProducer);
      }
    });

    // Remove this animal from all other animals' Food Sources
    animals.forEach(animal => {
      if (animal['Food Sources'].includes(animalName)) {
        const otherAnimalIndex = animals.findIndex(a => a.id === animal.id);
        if (otherAnimalIndex !== index) { // Don't update the animal we're removing
          const updatedAnimal = { ...animal };
          updatedAnimal['Food Sources'] = updatedAnimal['Food Sources'].filter(
            source => source !== animalName
          );
          updateAnimal(otherAnimalIndex, updatedAnimal);
        }
      }
    });

    const updated = animals.filter((_, i) => i !== index);
    setAnimals(updated);
  };

  const renderHint = (text) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-start gap-2 text-blue-300 text-xs sm:text-sm bg-blue-900/20 p-2 sm:p-3 rounded-lg"
    >
      <InformationCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
      <div>{text}</div>
    </motion.div>
  );

  const renderError = (error) => (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="flex items-center gap-1 text-red-400 text-xs sm:text-sm mt-1"
    >
      <ExclamationCircleIcon className="w-3 h-3 sm:w-4 sm:h-4" />
      {error}
    </motion.div>
  );

  const renderEatenByField = (item, index, updateFn) => (
    <div className="col-span-1 sm:col-span-12 grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 mt-3 sm:mt-0">
      <div className="col-span-1 sm:col-span-7">
        <AnimatePresence>
          {item['Eaten By'].length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="text-sm text-blue-300 bg-blue-900/20 p-2 sm:p-4 rounded-lg border border-blue-500/20"
            >
              <div className="font-medium mb-1 sm:mb-2">Eaten By:</div>
              <div className="text-xs text-blue-300/70 mb-2 sm:mb-3">Animals that consume this producer</div>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {item['Eaten By'].map((eater, idx) => (
                  <motion.div
                    key={`${eater}-${idx}`}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-medium bg-blue-900/30 text-blue-300 border border-blue-500/30"
                  >
                    <span className="truncate max-w-[120px] sm:max-w-[200px]">{eater}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      <div className="col-span-1 sm:col-span-5">
        <label className="block text-sm font-medium text-blue-300 h-10 sm:h-14">
          Eaten By
          <span className="block text-xs text-blue-400">Select animals that eat this producer</span>
        </label>
        
        <FoodSourceSelector
          selectedSources={item['Eaten By'].map(name => {
            // Find the index of the animal with this name and convert to letter
            const animalIndex = animals.findIndex(a => a.Name === name);
            return animalIndex >= 0 ? String.fromCharCode(65 + animalIndex) : '';
          }).filter(letter => letter)}
          onSourcesChange={(symbols) => {
            // Convert letters back to animal names
            const eaters = symbols.map(symbol => {
              if (/^[A-J]$/.test(symbol)) {
                const index = symbol.charCodeAt(0) - 65;
                return animals[index]?.Name || '';
              }
              return '';
            }).filter(name => name); // Remove empty strings
            
            // Update the producer's Eaten By list
            updateFn(index, { 'Eaten By': eaters });
            
            // Update Food Sources of all animals
            animals.forEach((animal, animalIndex) => {
              const shouldBeInFoodSources = eaters.includes(animal.Name);
              const currentSources = typeof animal['Food Sources'] === 'string'
                ? animal['Food Sources'].split(',').map(s => s.trim())
                : Array.isArray(animal['Food Sources'])
                  ? animal['Food Sources']
                  : [];
              const isInFoodSources = currentSources.includes(item.Name);
              
              if (shouldBeInFoodSources && !isInFoodSources) {
                // Add this producer to the animal's food sources
                const newSources = [...currentSources, item.Name];
                updateAnimal(animalIndex, {
                  'Food Sources': newSources.join(', ')
                });
              } else if (!shouldBeInFoodSources && isInFoodSources) {
                // Remove this producer from the animal's food sources
                const newSources = currentSources.filter(source => source !== item.Name);
                updateAnimal(animalIndex, {
                  'Food Sources': newSources.join(', ')
                });
              }
            });
          }}
          producerNames={[]} // Empty since we don't need producers here
          animalNames={animals.map(a => a.Name)}
          disabled={false}
        />
      </div>
    </div>
  );

  const renderSection = (title, items, addFn, updateFn, removeFn, isProducer = false) => {
    const isExpanded = expandedSections.includes(title);
    const itemLimit = isProducer ? 3 : 10;
    const itemCount = items.length;
    
    return (
      <motion.div
        initial={false}
        animate={{ backgroundColor: isExpanded ? 'rgba(255, 255, 255, 0.05)' : 'transparent' }}
        className="rounded-xl overflow-hidden mb-6 border border-blue-800/20"
      >
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-gray-900/50 to-blue-900/30 gap-2 sm:gap-0"
        >
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedSections(prev => 
                isExpanded 
                  ? prev.filter(section => section !== title)
                  : [...prev, title]
              );
            }}
          >
            <span className="text-2xl">
              {isProducer ? '🌿' : '🦁'}
            </span>
            <div>
              <h3 className="text-xl font-semibold text-white">{title}</h3>
              <div className="text-sm text-blue-400">
                {itemCount} / {itemLimit} {isProducer ? 'Producers' : 'Animals'}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <PrefillButton />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
                itemCount >= itemLimit 
                  ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-300'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (itemCount < itemLimit) {
                  addFn();
                  if (!isExpanded) {
                    setExpandedSections(prev => [...prev, title]);
                  }
                }
              }}
              disabled={itemCount >= itemLimit}
            >
              <PlusIcon className="w-4 h-4" />
              Add {isProducer ? 'Producer' : 'Animal'}
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="p-3 sm:p-4 space-y-4 bg-gradient-to-br from-gray-900/30 to-blue-900/20">
                {showHints && items.length === 0 && (
                  renderHint(isProducer 
                    ? "Add producers (plants) that provide calories to the ecosystem. Each producer must have calories available after being consumed."
                    : "Add animals. Animals eat in order of highest calories provided. Each animal must get enough calories from its food sources."
                  )
                )}
                
                {items.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-6 p-2 sm:p-4 rounded-lg bg-white/5 border border-blue-800/20 relative group"
                  >
                    <div className="col-span-1 sm:col-span-3">
                      <label className="block text-sm font-medium text-blue-300 h-10 sm:h-14">
                        {isProducer 
                          ? `Producer ${index + 1} Name`
                          : `Animal ${String.fromCharCode(65 + index)} Name`}
                        <span className="block text-xs text-blue-400">Required</span>
                      </label>
                      <input
                        type="text"
                        value={item.Name}
                        onChange={(e) => updateFn(index, { Name: e.target.value })}
                        className={`w-full h-9 sm:h-10 px-2 sm:px-3 py-1 sm:py-2 bg-gray-900/50 border ${
                          errors[`producer-${index}-name`] ? 'border-red-500/50' : 'border-blue-800/30'
                        } rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                      />
                      {errors[`producer-${index}-name`] && renderError(errors[`producer-${index}-name`])}
                    </div>
                    
                    {!isProducer && (
                      <div className="col-span-1 sm:col-span-2">
                        <label className="block text-sm font-medium text-blue-300 h-10 sm:h-14">
                          Calories Needed
                          <span className="block text-xs text-blue-400">Required</span>
                        </label>
                        <input
                          type="number"
                          value={item['Calories Needed']}
                          onChange={(e) => updateFn(index, { 'Calories Needed': e.target.value })}
                          className={`w-full h-9 sm:h-10 px-2 sm:px-3 py-1 sm:py-2 bg-gray-900/50 border ${
                            errors[`animal-${index}-caloriesIn`] ? 'border-red-500/50' : 'border-blue-800/30'
                          } rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                        />
                        {errors[`animal-${index}-caloriesIn`] && renderError(errors[`animal-${index}-caloriesIn`])}
                      </div>
                    )}
                    
                    <div className="col-span-1 sm:col-span-2">
                      <label className="block text-sm font-medium text-blue-300 h-10 sm:h-14">
                        Calories Provided
                        <span className="block text-xs text-blue-400">Provided</span>
                      </label>
                      <input
                        type="number"
                        value={item['Calories Provided']}
                        onChange={(e) => updateFn(index, { 'Calories Provided': e.target.value })}
                        className={`w-full h-9 sm:h-10 px-2 sm:px-3 py-1 sm:py-2 bg-gray-900/50 border ${
                          errors[`producer-${index}-calories`] ? 'border-red-500/50' : 'border-blue-800/30'
                        } rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                      />
                      {errors[`producer-${index}-calories`] && renderError(errors[`producer-${index}-calories`])}
                    </div>

                    {!isProducer && (
                      <>
                        <div className="col-span-1 sm:col-span-5">
                          <label className="block text-sm font-medium text-blue-300 h-10 sm:h-14">
                            Food Sources
                            <span className="block text-xs text-blue-400">Selected sources</span>
                          </label>
                          
                          <FoodSourceSelector
                            selectedSources={item['Food Sources'].map(getSymbolFromName).filter(Boolean)}
                            onSourcesChange={(symbols) => {
                              // Convert symbols back to names
                              const sources = symbols.map(symbol => {
                                if (/^[1-3]$/.test(symbol)) {
                                  // It's a producer
                                  const index = parseInt(symbol) - 1;
                                  return producers[index]?.Name || '';
                                } else if (/^[A-J]$/.test(symbol)) {
                                  // It's an animal
                                  const index = symbol.charCodeAt(0) - 65;
                                  return animals[index]?.Name || '';
                                }
                                return '';
                              }).filter(Boolean);

                              // Update the animal's food sources
                              updateAnimal(index, { 'Food Sources': sources });

                              // Update the 'Eaten By' lists of the selected sources
                              producers.forEach((producer, producerIndex) => {
                                const shouldBeInEatenBy = sources.includes(producer.Name);
                                const isInEatenBy = producer['Eaten By'].includes(item.Name);
                                
                                if (shouldBeInEatenBy && !isInEatenBy) {
                                  // Add this animal to the producer's eaten by list
                                  const newEatenBy = [...producer['Eaten By'], item.Name];
                                  updateProducer(producerIndex, { 'Eaten By': newEatenBy });
                                } else if (!shouldBeInEatenBy && isInEatenBy) {
                                  // Remove this animal from the producer's eaten by list
                                  const newEatenBy = producer['Eaten By'].filter(name => name !== item.Name);
                                  updateProducer(producerIndex, { 'Eaten By': newEatenBy });
                                }
                              });

                              // Update the 'Eaten By' lists of the selected animals
                              animals.forEach((animal, animalIndex) => {
                                if (animalIndex === index) return; // Skip the current animal
                                
                                const shouldBeInEatenBy = sources.includes(animal.Name);
                                const isInEatenBy = animal['Eaten By']?.includes(item.Name);
                                
                                if (shouldBeInEatenBy && !isInEatenBy) {
                                  // Add this animal to the other animal's eaten by list
                                  const newEatenBy = [...(animal['Eaten By'] || []), item.Name];
                                  updateAnimal(animalIndex, { 'Eaten By': newEatenBy });
                                } else if (!shouldBeInEatenBy && isInEatenBy) {
                                  // Remove this animal from the other animal's eaten by list
                                  const newEatenBy = (animal['Eaten By'] || []).filter(name => name !== item.Name);
                                  updateAnimal(animalIndex, { 'Eaten By': newEatenBy });
                                }
                              });
                            }}
                            producerNames={nameMapping.producers}
                            animalNames={nameMapping.animals}
                            disabled={false}
                          />
                        </div>

                        <div className="col-span-1 sm:col-span-7">
                          <AnimatePresence>
                            {item['Food Sources'] && (
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                className="text-sm text-blue-300 bg-blue-900/20 p-2 sm:p-4 rounded-lg border border-blue-500/20"
                              >
                                <div className="font-medium mb-1 sm:mb-2">Selected Sources:</div>
                                <div className="text-xs text-blue-300/70 mb-2 sm:mb-3">Food sources for this animal</div>
                                <div className="flex flex-wrap gap-1 sm:gap-2">
                                  {item['Food Sources'].map((source, idx) => (
                                    <motion.div
                                      key={`${source}-${idx}`}
                                      initial={{ scale: 0, opacity: 0 }}
                                      animate={{ scale: 1, opacity: 1 }}
                                      exit={{ scale: 0, opacity: 0 }}
                                      className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-medium bg-blue-900/30 text-blue-300 border border-blue-500/30"
                                    >
                                      <span className="truncate max-w-[120px] sm:max-w-[200px]">{source}</span>
                                    </motion.div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </>
                    )}
                    
                    {isProducer && renderEatenByField(item, index, updateFn)}

                    {((isProducer && producers.length > 1) || (!isProducer && animals.length > 10)) && (
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => removeFn(index)}
                        className="absolute -right-3 -top-3 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MinusIcon className="w-4 h-4" />
                      </motion.button>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const renderMetadataSection = () => (
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 p-3 sm:p-4 bg-white/5 rounded-lg border border-blue-800/20 mb-6">
      <div className="sm:col-span-4">
        <label className="block text-sm font-medium text-blue-300 mb-2 sm:h-14">
          Ecosystem Name
          <span className="block text-xs text-blue-400">Required</span>
        </label>
        <input
          type="text"
          value={metadata.ecosystemName}
          onChange={(e) => setMetadata(prev => ({ ...prev, ecosystemName: e.target.value }))}
          className={`w-full h-10 px-3 py-2 bg-gray-900/50 border ${
            errors.ecosystemName ? 'border-red-500/50' : 'border-blue-800/30'
          } rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
        />
        {errors.ecosystemName && renderError(errors.ecosystemName)}
      </div>

      <div className="sm:col-span-4">
        <label className="block text-sm font-medium text-blue-300 mb-2 sm:h-14">
          Depth/Elevation Range (meters)
          <span className="block text-xs text-blue-400">Required</span>
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <input
              type="number"
              value={metadata.minDepth}
              onChange={(e) => setMetadata(prev => ({ ...prev, minDepth: e.target.value }))}
              className={`w-full h-10 px-3 py-2 bg-gray-900/50 border ${
                errors.minDepth || errors.depth ? 'border-red-500/50' : 'border-blue-800/30'
              } rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
            />
            {errors.minDepth && renderError(errors.minDepth)}
          </div>
          <div className="flex-1">
            <input
              type="number"
              value={metadata.maxDepth}
              onChange={(e) => setMetadata(prev => ({ ...prev, maxDepth: e.target.value }))}
              className={`w-full h-10 px-3 py-2 bg-gray-900/50 border ${
                errors.maxDepth || errors.depth ? 'border-red-500/50' : 'border-blue-800/30'
              } rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
            />
            {errors.maxDepth && renderError(errors.maxDepth)}
          </div>
        </div>
        {errors.depth && renderError(errors.depth)}
      </div>

      <div className="sm:col-span-4">
        <label className="block text-sm font-medium text-blue-300 mb-2 sm:h-14">
          Temperature Range (°C)
          <span className="block text-xs text-blue-400">Required</span>
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <input
              type="number"
              value={metadata.minTemp}
              onChange={(e) => setMetadata(prev => ({ ...prev, minTemp: e.target.value }))}
              className={`w-full h-10 px-3 py-2 bg-gray-900/50 border ${
                errors.minTemp || errors.temp ? 'border-red-500/50' : 'border-blue-800/30'
              } rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
            />
            {errors.minTemp && renderError(errors.minTemp)}
          </div>
          <div className="flex-1">
            <input
              type="number"
              value={metadata.maxTemp}
              onChange={(e) => setMetadata(prev => ({ ...prev, maxTemp: e.target.value }))}
              className={`w-full h-10 px-3 py-2 bg-gray-900/50 border ${
                errors.maxTemp || errors.temp ? 'border-red-500/50' : 'border-blue-800/30'
              } rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
            />
            {errors.maxTemp && renderError(errors.maxTemp)}
          </div>
        </div>
        {errors.temp && renderError(errors.temp)}
      </div>
    </div>
  );

  // Add PrefillButton component
  const PrefillButton = () => (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handlePrefill}
      className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 rounded-lg text-sm font-medium transition-colors"
    >
      Prefill with Coral Ecosystem 1
    </motion.button>
  );

  // Add solve handler
  const handleSolve = async () => {
    // Validate data first
    if (!validateData()) {
      return;
    }
    setIsSolving(true);
    setSolveProgress(0);
    // Clean up animals' food sources
    const stripPrefix = (name) => name.replace(/^Producer \d+ - /, '').replace(/^Animal [A-J] - /, '').trim();
    const validProducerNames = producers.map(p => p.Name);
    const validAnimalNames = animals.map(a => a.Name);
    const validNames = new Set([...validProducerNames, ...validAnimalNames]);
    const cleanedAnimals = animals.map((a) => {
      let foodSourcesArr = [];
      if (Array.isArray(a['Food Sources'])) {
        foodSourcesArr = a['Food Sources'].map(stripPrefix).map(s => s.trim()).filter(Boolean);
      } else if (typeof a['Food Sources'] === 'string') {
        foodSourcesArr = a['Food Sources']
          .split(',')
          .map(stripPrefix)
          .map(s => s.trim())
          .filter(Boolean);
      }
      foodSourcesArr = foodSourcesArr.filter(name => validNames.has(name));
      return {
        Name: a.Name,
        'Calories Needed': a['Calories Needed'],
        'Calories Provided': a['Calories Provided'],
        'Food Sources': foodSourcesArr
      };
    });
    const cleanedProducers = producers.map((p) => {
      return {
        Name: p.Name,
        'Calories Provided': p['Calories Provided'],
        'Eaten By': Array.isArray(p['Eaten By']) ? p['Eaten By'].map(stripPrefix).map(s => s.trim()).filter(Boolean) : []
      };
    });
    // Notify parent only when solving
    onDataChange({
      metadata,
      producers: cleanedProducers,
      animals: cleanedAnimals,
      solved: true
    });
    setIsSolving(false);
  };

  // Update the information panel text to be more explicit
  const renderInformationPanel = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2 sm:gap-0">
        <h4 className="text-lg font-semibold text-blue-300">Demo Version - Coral Ecosystem 1 Only</h4>
        <button 
          onClick={() => setShowHints(false)}
          className="text-blue-400 hover:text-blue-300 text-sm"
        >
          Hide
        </button>
      </div>
      <ul className="space-y-2 text-blue-200 text-sm">
        <li>• This is a demo version that only works with Coral Ecosystem 1 data</li>
        <li>• Click the "Prefill with Coral Ecosystem 1" button to load the correct data</li>
        <li>• You cannot enter your own data in this demo version</li>
        <li>• Click "Solve Ecosystem" after using the prefill button</li>
      </ul>
    </motion.div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 -mx-6 sm:mx-0 p-3 sm:p-4 rounded-xl border border-blue-800/20"
      style={{
        background: 'linear-gradient(135deg, rgba(30,41,59,0.85) 0%, rgba(30,58,138,0.75) 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25), 0 4px 8px rgba(0,0,0,0.18)',
        borderRadius: '1rem',
        border: '1px solid rgba(59,130,246,0.12)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        overflow: 'hidden',
      }}
    >
      {/* Use the new information panel */}
      {showHints && renderInformationPanel()}

      {/* Metadata Section */}
      {renderMetadataSection()}

      {/* Global Errors */}
      {(errors.producers || errors.animals || errors.circularDependency || errors.invalidFoodSource) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-900/20 border border-red-500/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6"
        >
          {errors.producers && <div className="text-red-400 mb-2">{errors.producers}</div>}
          {errors.animals && <div className="text-red-400 mb-2">{errors.animals}</div>}
          {errors.circularDependency && (
            <div className="text-red-400 mb-2">
              <div className="font-medium">Circular Dependency Error:</div>
              {errors.circularDependency}
            </div>
          )}
          {errors.invalidFoodSource && (
            <div className="text-red-400">
              <div className="font-medium">Invalid Food Source Error:</div>
              {errors.invalidFoodSource}
            </div>
          )}
        </motion.div>
      )}

      {/* Producers Section */}
      {renderSection('Producers', producers, addProducer, updateProducer, removeProducer, true)}
      
      {/* Animals Section */}
      {renderSection('Animals', animals, addAnimal, updateAnimal, removeAnimal, false)}

      {/* Add SolveEcosystemButton */}
      <SolveEcosystemButton
        onSolve={handleSolve}
        isLoading={isSolving}
        progress={solveProgress}
      />
    </div>
  );
};

export default ManualEntry;